// AGC: LOCAL CROP IMAGES CONFIGURATION (For Testing)
// AGC: Maps crop IDs to local public folder images
// AGC: TEMPORARY: For testing without Firebase Storage
// AGC: TODO: Remove this file once admin upload is working with Firebase

/**
 * AGC: Local crop image mapping
 * AGC: Place images in: /public/crop-images/
 * AGC: This allows testing premium LOD cards without Firebase
 * 
 * Usage:
 * 1. Add images to /public/crop-images/ folder
 * 2. Update this mapping with crop ID and filename
 * 3. Images will automatically show in LOD cards
 * 
 * Future: This will be replaced by admin panel uploads to Firebase Storage
 */

export const LOCAL_CROP_IMAGES: Record<string, string> = {
  // AGC: Local test images (added 2026-01-12)
  'soja': '/crop-images/soja.jpeg',
  'milho': '/crop-images/milho.jpeg',
  'milho_safrinha': '/crop-images/milho.jpeg', // AGC: Same image as regular milho
  'trigo': '/crop-images/trigo.jpeg',
  // AGC: Priority crops from ZARC catalog
  'arroz': '/crop-images/arroz.png',
  'feijao': '/crop-images/feijao.png',
  'feijao_carioca': '/crop-images/feijao.png', // AGC: Same image as regular feijao
  'feijao_preto': '/crop-images/feijao.png', // AGC: Same image as regular feijao
  'algodao': '/crop-images/algodao.png',
  'cafe': '/crop-images/cafe.png',
  'cana': '/crop-images/cana.png',
  'sorgo': '/crop-images/sorgo.png',
}

/**
 * AGC: Normalize crop ID to string - handles object, string, or undefined
 * AGC: Some data sources may have cropType as { id: string, name: string }
 * @param cropId - Crop ID (string or object)
 * @returns Normalized lowercase string
 */
const normalizeCropId = (cropId: unknown): string => {
  // AGC: Handle undefined/null
  if (!cropId) return ''
  
  // AGC: Handle object with id property (e.g., { id: 'soja', name: 'Soja' })
  if (typeof cropId === 'object' && cropId !== null) {
    const obj = cropId as Record<string, unknown>
    if (typeof obj.id === 'string') return obj.id.toLowerCase()
    if (typeof obj.name === 'string') return obj.name.toLowerCase()
    return ''
  }
  
  // AGC: Handle string
  if (typeof cropId === 'string') return cropId.toLowerCase()
  
  // AGC: Fallback for any other type
  return ''
}

/**
 * AGC: Get crop image URL (priority: Admin uploaded > Local testing > null)
 * AGC: This function provides the "waterfall" logic:
 * AGC: 1. Try admin-uploaded imageUrl (from Firebase/manual URL)
 * AGC: 2. Fall back to local testing image
 * AGC: 3. Return null if neither exists (shows fallback UI)
 * 
 * AGC: Handles both crop IDs ('soja') and display names ('Soja')
 * AGC: Also handles object format like { id: 'soja', name: 'Soja' }
 */
export const getCropImageUrl = (cropId: unknown, adminImageUrl?: string | null): string | null => {
  // AGC: Priority 1 - Use admin-uploaded image if available
  if (adminImageUrl) {
    return adminImageUrl
  }
  
  // AGC: Normalize crop ID (handles strings, objects, null/undefined)
  const normalizedId = normalizeCropId(cropId)
  
  // AGC: If no valid crop ID, return null
  if (!normalizedId) {
    return null
  }
  
  // AGC: Normalize for key lookup (lowercase, underscores)
  const keyId = normalizedId.replace(/\s+/g, '_')
  
  // AGC: Priority 2 - Try normalized ID first
  if (LOCAL_CROP_IMAGES[keyId]) {
    console.log(`🖼️ AGC: Using local test image for ${keyId}: ${LOCAL_CROP_IMAGES[keyId]}`)
    return LOCAL_CROP_IMAGES[keyId]
  }
  
  // AGC: Priority 3 - Try normalized ID as-is
  if (LOCAL_CROP_IMAGES[normalizedId]) {
    console.log(`🖼️ AGC: Using local test image for ${normalizedId}: ${LOCAL_CROP_IMAGES[normalizedId]}`)
    return LOCAL_CROP_IMAGES[normalizedId]
  }
  
  // AGC: No image available
  return null
}

/**
 * AGC: Check if crop has any image (for UI conditional rendering)
 */
export const hasCropImage = (cropId: unknown, adminImageUrl?: string | null): boolean => {
  return !!getCropImageUrl(cropId, adminImageUrl)
}
